import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const VerifyPage: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Verifying your email...');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const verifyEmail = async () => {
      // Get the email from URL query parameters (before the hash)
      const urlParams = new URLSearchParams(window.location.search);
      const email = urlParams.get('email');
      
      if (!email) {
        setStatus('error');
        setMessage('Invalid verification link. Email parameter is missing.');
        return;
      }

      try {
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/verify-account`, { email });
        
        if (response.data.token) {
          // Store the token and user data - ensure user data is valid before storing
          localStorage.setItem('authToken', response.data.token);
          
          // Only store user data if it exists
          if (response.data.user) {
            localStorage.setItem('user', JSON.stringify(response.data.user));
          } else {
            // If no user data is returned, create a minimal user object with the email
            const basicUserData = { email: email };
            localStorage.setItem('user', JSON.stringify(basicUserData));
          }
          
          setStatus('success');
          setMessage('Your email has been successfully verified!');
          
          // Redirect to dashboard after 2 seconds
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        } else {
          throw new Error('Verification successful but no token received');
        }
      } catch (error) {
        setStatus('error');
        setMessage(error instanceof Error 
          ? `Verification failed: ${error.message}` 
          : 'Verification failed. Please try again or contact support.');
      }
    };

    verifyEmail();
  }, [navigate]);

  return (
    <div className="page page-center">
      <div className="container container-tight py-4">
        <div className="card card-md">
          <div className="card-body text-center py-4 p-sm-5">
            {status === 'loading' && (
              <>
                <div className="spinner-border mb-3" role="status"></div>
                <h1 className="mt-5">Email Verification</h1>
                <p className="text-muted">{message}</p>
              </>
            )}
            
            {status === 'success' && (
              <>
                <span className="avatar avatar-xl mb-3 bg-green-lt">
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-check" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M5 12l5 5l10 -10"></path>
                  </svg>
                </span>
                <h1 className="mt-5">Verification Successful</h1>
                <p className="text-muted">{message}</p>
                <p className="text-muted">Redirecting to dashboard...</p>
              </>
            )}
            
            {status === 'error' && (
              <>
                <span className="avatar avatar-xl mb-3 bg-red-lt">
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-alert-triangle" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M12 9v2m0 4v.01"></path>
                    <path d="M5 19h14a2 2 0 0 0 1.84 -2.75l-7.1 -12.25a2 2 0 0 0 -3.5 0l-7.1 12.25a2 2 0 0 0 1.75 2.75"></path>
                  </svg>
                </span>
                <h1 className="mt-5">Verification Failed</h1>
                <p className="text-muted">{message}</p>
                <div className="mt-4">
                  <Link to="/login" className="btn btn-primary">Back to Login</Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyPage;